import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { Ionicons }       from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface Step {
  icon: string;
}

interface GuideMeta {
  id:        string;
  icon:      string;
  color:     [string, string];
  stepCount: number;
  hasNote:   boolean;
}

interface Guide {
  id:       string;
  icon:     string;
  tabLabel: string;
  title:    string;
  color:    [string, string];
  steps:    { icon: string; text: string }[];
  note?:    string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTADONNÉES DES GUIDES (icônes, couleurs, structure — non traduisibles)
// ─────────────────────────────────────────────────────────────────────────────

const GUIDES_META: GuideMeta[] = [
  {
    id:        'create',
    icon:      'person-add-outline',
    color:     ['#11998e', '#38ef7d'],
    stepCount: 8,
    hasNote:   false,
  },
  {
    id:        'login',
    icon:      'log-in-outline',
    color:     ['#FF0080', '#FF8C00'],
    stepCount: 5,
    hasNote:   false,
  },
  {
    id:        'reset',
    icon:      'key-outline',
    color:     ['#F59E0B', '#EF4444'],
    stepCount: 6,
    hasNote:   true,
  },
  {
    id:        'google',
    icon:      'logo-google',
    color:     ['#4285F4', '#34A853'],
    stepCount: 4,
    hasNote:   true,
  },
  {
    id:        'verify',
    icon:      'mail-open-outline',
    color:     ['#00c6ff', '#0072ff'],
    stepCount: 5,
    hasNote:   true,
  },
];

// Icônes des étapes par guide (index 0-based, non traduisibles)
const STEP_ICONS: Record<string, string[]> = {
  create: [
    'person-outline',
    'text-outline',
    'mail-outline',
    'lock-closed-outline',
    'checkbox-outline',
    'send-outline',
    'mail-open-outline',
    'checkmark-done-circle-outline',
  ],
  login: [
    'mail-outline',
    'lock-closed-outline',
    'eye-outline',
    'arrow-forward-circle-outline',
    'home-outline',
  ],
  reset: [
    'help-circle-outline',
    'mail-outline',
    'send-outline',
    'mail-open-outline',
    'link-outline',
    'lock-closed-outline',
  ],
  google: [
    'logo-google',
    'person-outline',
    'shield-checkmark-outline',
    'home-outline',
  ],
  verify: [
    'mail-outline',
    'search-outline',
    'link-outline',
    'arrow-back-circle-outline',
    'checkmark-circle-outline',
  ],
};

function getGuides(t: (key: string) => string): Guide[] {
  return GUIDES_META.map((meta) => {
    const base = `guide.guides.${meta.id}`;
    const steps = Array.from({ length: meta.stepCount }, (_, i) => ({
      icon: STEP_ICONS[meta.id][i],
      text: t(`${base}.step${i + 1}`),
    }));
    return {
      id:       meta.id,
      icon:     meta.icon,
      color:    meta.color,
      tabLabel: t(`${base}.tabLabel`),
      title:    t(`${base}.title`),
      steps,
      note: meta.hasNote ? t(`${base}.note`) : undefined,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT
// ─────────────────────────────────────────────────────────────────────────────

export default function GuideUtilisation({ visible, onClose }: Props) {
  const { t } = useTranslation();
  const GUIDES = getGuides(t);

  const [activeId, setActiveId] = useState(GUIDES[0].id);
  const guide = GUIDES.find(g => g.id === activeId) ?? GUIDES[0];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.sheet}>

          {/* ── En-tête ─────────────────────────────────────────────── */}
          <LinearGradient colors={['#8A2BE2', '#4B0082']} style={styles.header}>
            <Ionicons name="help-circle" size={26} color="#fff" />
            <Text style={styles.headerTitle}>{t('guide.header.title')}</Text>
            <TouchableOpacity
              onPress={onClose}
              style={styles.closeBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              activeOpacity={0.7}
            >
              <Ionicons name="close-circle" size={28} color="rgba(255,255,255,0.85)" />
            </TouchableOpacity>
          </LinearGradient>

          {/* ── Tabs ────────────────────────────────────────────────── */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabBar}
            contentContainerStyle={styles.tabBarInner}
          >
            {GUIDES.map(g => {
              const active = g.id === activeId;
              return (
                <TouchableOpacity
                  key={g.id}
                  style={[styles.tab, active && styles.tabActive]}
                  onPress={() => setActiveId(g.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={g.icon as any}
                    size={15}
                    color={active ? '#8A2BE2' : '#BBB'}
                    style={{ marginBottom: 3 }}
                  />
                  <Text style={[styles.tabLabel, active && styles.tabLabelActive]}>
                    {g.tabLabel}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          {/* ── Corps ───────────────────────────────────────────────── */}
          <ScrollView
            style={styles.body}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.bodyContent}
          >
            {/* Badge titre */}
            <LinearGradient colors={guide.color} style={styles.titleBadge}>
              <Ionicons name={guide.icon as any} size={22} color="#fff" />
              <Text style={styles.titleBadgeText}>{guide.title}</Text>
            </LinearGradient>

            {/* Étapes */}
            <View style={styles.stepsWrap}>
              {guide.steps.map((step, i) => (
                <View key={i} style={styles.stepRow}>

                  {/* Gauche : numéro + trait de connexion */}
                  <View style={styles.stepLeft}>
                    <LinearGradient colors={guide.color} style={styles.stepDot}>
                      <Text style={styles.stepNum}>{i + 1}</Text>
                    </LinearGradient>
                    {i < guide.steps.length - 1 && (
                      <View style={styles.stepConnector} />
                    )}
                  </View>

                  {/* Droite : icône + texte */}
                  <View style={styles.stepRight}>
                    <View style={styles.stepIconBubble}>
                      <Ionicons name={step.icon as any} size={16} color="#8A2BE2" />
                    </View>
                    <Text style={styles.stepText}>{step.text}</Text>
                  </View>

                </View>
              ))}
            </View>

            {/* Note / conseil */}
            {guide.note && (
              <View style={styles.noteBox}>
                <Text style={styles.noteText}>{guide.note}</Text>
              </View>
            )}
          </ScrollView>

          {/* ── Bouton fermer ───────────────────────────────────────── */}
          <TouchableOpacity
            onPress={onClose}
            activeOpacity={0.85}
            style={styles.footerWrapper}
          >
            <LinearGradient colors={['#8A2BE2', '#4B0082']} style={styles.footerBtn}>
              <Ionicons name="checkmark-circle-outline" size={20} color="#fff" />
              <Text style={styles.footerBtnText}>{t('guide.header.gotIt')}</Text>
            </LinearGradient>
          </TouchableOpacity>

        </View>
      </View>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({

  /* Fond semi-transparent */
  overlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent:  'flex-end',
  },

  /* Feuille (bottom sheet) */
  sheet: {
    backgroundColor:      '#FAFAFA',
    borderTopLeftRadius:  28,
    borderTopRightRadius: 28,
    maxHeight:            '90%',
    overflow:             'hidden',
  },

  /* ── En-tête ── */
  header: {
    flexDirection:     'row',
    alignItems:        'center',
    paddingHorizontal: 20,
    paddingVertical:   16,
  },
  headerTitle: {
    flex:          1,
    fontSize:      18,
    fontWeight:    '700',
    color:         '#fff',
    marginLeft:    10,
    letterSpacing: 0.3,
  },
  closeBtn: { padding: 2 },

  /* ── Tabs ── */
  tabBar: {
    backgroundColor:   '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#EDE0FF',
  },
  tabBarInner: {
    paddingHorizontal: 12,
    paddingVertical:    8,
    flexDirection:     'row',
    alignItems:        'center',
  },
  tab: {
    alignItems:        'center',
    justifyContent:    'center',
    paddingHorizontal: 14,
    paddingVertical:    7,
    borderRadius:      20,
    backgroundColor:   '#F5F5F5',
    marginRight:        8,
  },
  tabActive: {
    backgroundColor: '#EDE0FF',
    borderWidth:     1.5,
    borderColor:     '#C09EFF',
  },
  tabLabel: {
    fontSize:   11,
    color:      '#BBB',
    fontWeight: '600',
  },
  tabLabelActive: {
    color: '#8A2BE2',
  },

  /* ── Corps ── */
  body:        { paddingHorizontal: 20 },
  bodyContent: { paddingTop: 20, paddingBottom: 12 },

  /* Badge titre du guide */
  titleBadge: {
    flexDirection:     'row',
    alignItems:        'center',
    borderRadius:      14,
    paddingVertical:   13,
    paddingHorizontal: 16,
    marginBottom:      24,
  },
  titleBadgeText: {
    flex:       1,
    fontSize:   15,
    fontWeight: '700',
    color:      '#fff',
    marginLeft: 10,
  },

  /* ── Étapes ── */
  stepsWrap: {},

  stepRow: {
    flexDirection: 'row',
  },

  /* Colonne gauche : numéro + trait */
  stepLeft: {
    width:       36,
    alignItems:  'center',
    marginRight: 12,
  },
  stepDot: {
    width:          30,
    height:         30,
    borderRadius:   15,
    justifyContent: 'center',
    alignItems:     'center',
  },
  stepNum: {
    fontSize:   13,
    fontWeight: '800',
    color:      '#fff',
  },
  stepConnector: {
    width:           2,
    flex:            1,
    minHeight:       16,
    backgroundColor: '#DDD0FF',
    marginVertical:   4,
  },

  /* Colonne droite : icône + texte */
  stepRight: {
    flex:          1,
    flexDirection: 'row',
    alignItems:    'flex-start',
    paddingBottom: 20,
  },
  stepIconBubble: {
    width:           32,
    height:          32,
    borderRadius:    10,
    backgroundColor: '#EDE0FF',
    justifyContent:  'center',
    alignItems:      'center',
    marginRight:     10,
    flexShrink:       0,
  },
  stepText: {
    flex:       1,
    fontSize:   14,
    color:      '#444',
    lineHeight: 20,
    paddingTop:  6,
  },

  /* Note / conseil */
  noteBox: {
    backgroundColor: '#FFFBEB',
    borderRadius:    12,
    padding:         14,
    borderLeftWidth:  4,
    borderLeftColor: '#F59E0B',
    marginTop:        4,
    marginBottom:     8,
  },
  noteText: {
    fontSize:   13,
    color:      '#78510A',
    lineHeight: 20,
  },

  /* ── Bouton fermer ── */
  footerWrapper: {
    margin: 16,
  },
  footerBtn: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'center',
    paddingVertical: 15,
    borderRadius:   14,
    gap:             8,
  },
  footerBtnText: {
    fontSize:    16,
    fontWeight:  '700',
    color:       '#fff',
    marginLeft:   6,
  },
});
